import { AloftDate, expected } from '@aloft/toolkit-shared';
import { useModal } from '@aloft/toolkit-sui';

import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { useAutofillProperty } from '../../hooks/useAutofillProperty.ts';
import { useZodForm } from '../../hooks/useZodForm.ts';
import { submitEditPropertyDetailsForm } from '../../redux/features/actions/submitEditPropertyDetailsForm.ts';
import { selectEffectiveDate, selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import {
  selectAddress,
  selectCoordinatesById,
} from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectTransactionById } from '../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { EditPropertyDetailsModalContent } from './internal/EditPropertyDetailsModalContent.tsx';
import { schEditPropertyDetailsForm } from './internal/types.ts';

const editDetailModals = {
  EditDetails: {
    title: 'Edit Details',
    addressLabel: 'Property Address',
    showWarning: true,
    lockAddress: false,
  },
  MLSInfoForSubject: {
    title: 'Edit Subject Property',
    addressLabel: 'Subject Property Address',
    showWarning: true,
    lockAddress: false,
  },
  MLSInfoForComp: {
    title: 'Edit Comparable Property',
    addressLabel: 'Comp Address',
    showWarning: true,
    lockAddress: false,
  },
};

type EditDetailModalVariation = keyof typeof editDetailModals;

export const useEditPropertyDetailsModal = (
  compIDOrSubject: string,
  editDetailsModalType: EditDetailModalVariation = 'EditDetails',
) => {
  const EditPropertyDetailModal = useModal();
  const dispatch = useAppDispatch();
  const address = useAppSelector((state) => selectAddress(state, compIDOrSubject));
  const { mls, mlsListingID } = useAppSelector((state) =>
    selectTransactionById(state, compIDOrSubject),
  ) ?? { mls: undefined, mlsListingID: undefined };
  const coordinates = useAppSelector((state) => selectCoordinatesById(state, compIDOrSubject));

  const { title, addressLabel, showWarning, lockAddress } = editDetailModals[editDetailsModalType];
  const [mlsInfoChanged, setMlsInfoChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const autofillProperty = useAutofillProperty();
  const effectiveDate = useAppSelector((state) => selectEffectiveDate(state));
  const orderID = useAppSelector(selectOrder).id;

  const form = useZodForm({
    schema: schEditPropertyDetailsForm,
    defaultValues: {
      address,
      mls,
      mlsListingID,
      coordinates,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const formMls = useWatch({
    control: form.control,
    name: 'mls',
  });

  const formMlsListingID = useWatch({
    control: form.control,
    name: 'mlsListingID',
  });

  useEffect(() => {
    if (formMls !== mls || formMlsListingID !== mlsListingID) {
      setMlsInfoChanged(true);
    }
  }, [formMls, formMlsListingID, mls, mlsListingID]);

  // Make sure that we properly update our defaultValues if they are changed
  // outside of this particular form (changes could come from the sidebar mls
  // info update or edit details from the triple dots in the property
  // headers). This also fixes the issue of making sure that the proper
  // defaults are set even for the same form, and allowing a reset on close so
  // that when the form is reopened after a cancel it's back to the proper state.
  useEffect(() => {
    form.reset({
      address,
      mls,
      mlsListingID,
      coordinates,
    });
    setMlsInfoChanged(false);
  }, [form, address, mls, mlsListingID, coordinates]);

  const submit = form.handleSubmit(
    async (data) => {
      if (mlsInfoChanged) {
        setIsLoading(true);
        const propertyData = await autofillProperty({
          address: data.address,
          mlsListingInfo:
            data.mls && data.mlsListingID
              ? {
                  mls: data.mls,
                  mlsListingID: data.mlsListingID,
                }
              : undefined,
          orderID,
          effectiveDate: AloftDate.schema.parse(expected(effectiveDate)).toJSON(),
        });
        dispatch(
          submitEditPropertyDetailsForm({
            id: compIDOrSubject,
            ...data,
            newAutofilledData: propertyData,
          }),
        );
      } else {
        dispatch(submitEditPropertyDetailsForm({ id: compIDOrSubject, ...data }));
      }
      setIsLoading(false);
      handleClose();
    },
    (errPayload) => console.warn(errPayload),
  );

  const handleClose = () => {
    EditPropertyDetailModal.closeModal();
    form.reset();
  };

  return {
    modal: (
      <EditPropertyDetailModal.Modal
        size="xl"
        isOpen={EditPropertyDetailModal.isModalOpen}
        onClose={handleClose}
        closeOnEsc
        title={title}
        primaryAction={{
          id: 'edit-comp-details-btn',
          text: 'Save',
          onClick: () => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            submit();
          },
          isLoading,
        }}
        secondaryAction={{
          text: 'Cancel',
          onClick: handleClose,
        }}
      >
        <EditPropertyDetailsModalContent
          form={form}
          addressLabel={addressLabel}
          showWarning={showWarning}
          lockAddress={lockAddress}
        />
      </EditPropertyDetailModal.Modal>
    ),
    props: EditPropertyDetailModal,
  };
};
